import React, { useMemo, useState } from "react";
import { LANGUAGES } from "../DropdownOptions/LanguageEthnicityGender";

export default function LanguagesEditModal({
  initialSelectedNames = [],
  onClose,
  onSave,
}) {
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const initial = new Set(
    (initialSelectedNames ?? []).map((n) => String(n).trim().toLowerCase()),
  );
  const [selected, setSelected] = useState(
    new Set(
      LANGUAGES.filter((l) => initial.has(l.name.toLowerCase())).map(
        (l) => l.name,
      ),
    ),
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return LANGUAGES;
    return LANGUAGES.filter((l) => l.name.toLowerCase().includes(q));
  }, [query]);

  const toggle = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      await onSave(Array.from(selected)); // pass language names
    } catch (ex) {
      setErr(ex?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Add Languages</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <input
            type="text"
            placeholder="Search languages…"
            className="w-full rounded-md border px-3 py-2"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          <div className="max-h-72 overflow-auto rounded border p-3 space-y-2">
            {filtered.map((l) => (
              <label key={l.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selected.has(l.name)}
                  onChange={() => toggle(l.name)}
                />
                <span>{l.name}</span>
              </label>
            ))}
            {filtered.length === 0 && (
              <p className="text-sm text-gray-500">No matches.</p>
            )}
          </div>

          {err && <p className="text-sm text-red-600">{err}</p>}

          <div className="mt-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded border px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
